import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Snowflake, CreditCard, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Subscription() {
  const { auth, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setPlan(r.data.monthly)).catch(() => {});
  }, []);

  const subscribe = async () => {
    if (!window.Razorpay) { toast.error("Payment library not loaded — please refresh and try again."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/razorpay/order", { package_id: "monthly" });
      const rzp = new window.Razorpay({
        key: data.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Reefer Sathi",
        description: data.name,
        prefill: data.prefill,
        theme: { color: "#0e8ba1" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await checkAuth();
            toast.success("Payment successful — your subscription is active!");
            navigate("/app");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed. Please contact support.");
          } finally { setLoading(false); }
        },
        modal: { ondismiss: () => { setLoading(false); toast.message("Checkout cancelled — no charge was made."); } },
      });
      rzp.on("payment.failed", (r) => { setLoading(false); toast.error(r?.error?.description || "Payment failed. Please try again."); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const perks = [
    "Unlimited load & truck postings",
    "Full access to the live freight board",
    "One-tap WhatsApp connect on every posting",
    "In-app messaging with all transporters",
    "Instant search across routes & temperature",
  ];

  const statusLabel = auth?.is_owner
    ? "Owner account — full access"
    : auth?.sub_active
      ? `Active until ${auth.subscription_ends_at ? new Date(auth.subscription_ends_at).toLocaleDateString() : ""}`
      : auth?.trial_active
        ? `Free trial until ${auth.trial_ends_at ? new Date(auth.trial_ends_at).toLocaleDateString() : ""}`
        : "No active subscription";

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-1">Subscription</h1>
      <p className="text-muted-foreground text-sm mb-6">Keep your team connected to the cold-chain network.</p>

      <div data-testid="subscription-status" className="rounded-xl border border-border bg-card/40 p-4 mb-6 flex items-center gap-3">
        <ShieldCheck className={`w-5 h-5 ${auth?.has_access ? "text-emerald-400" : "text-amber-400"}`} />
        <span className="text-sm">Status: <b>{statusLabel}</b></span>
      </div>

      <div className="rounded-2xl border border-primary/30 bg-card/60 overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Snowflake className="w-5 h-5 text-primary" />
            </div>
            <span className="font-heading font-bold text-xl">Monthly Pro</span>
          </div>
          <div className="flex items-end gap-1 mb-6">
            <span className="font-heading font-extrabold text-5xl font-mono-num">₹{plan ? plan.amount : 499}</span>
            <span className="text-muted-foreground mb-2">/ month</span>
          </div>
          <ul className="space-y-3 mb-8">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm">
                <span className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-primary" /></span>
                {p}
              </li>
            ))}
          </ul>
          <Button data-testid="subscribe-btn" onClick={subscribe} disabled={loading || auth?.is_owner}
                  className="w-full rounded-full h-12 font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
            <CreditCard className="w-4 h-4 mr-2" />
            {auth?.is_owner ? "Owner — no payment needed" : loading ? "Processing…" : auth?.sub_active ? "Extend / Renew" : "Subscribe now"}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-3">Secured by Razorpay · Cancel anytime.</p>
        </div>
      </div>
      <p className="text-center text-sm text-muted-foreground mt-6">
        Need help? <a href="mailto:info@reefersathi.com" data-testid="subscription-contact-link" className="text-primary hover:opacity-80">Contact us</a>
      </p>
    </div>
  );
}
